<template>
    <div>
        <page-header :text="$t('navigation.systems.hydraulics')" :button="{ text: $t('buttons.reload'), emitEvent: 'reloadData', ability: true }" :icon="'action-sync'" @reloadData="reload" />

        <page-subnavigation :items="allSystemLinks" />

        <div>
            <div class="tabs slab-mobile--large--bottom" v-if="zones.length > 0">
                <div class="tabs__header">
                    <div v-for="(zone, index) in zones" :key="zone.name" class="tabs__header__item" :class="{ active: index === activeZoneIndex }" @click="switchTab(index)">
                        {{ zone.name }}
                    </div>
                </div>
                <div class="tabs__body" v-for="(zone, index) in zones" :key="zone.name">
                    <div class="tabs__content" v-if="index === activeZoneIndex">
                        <div class="svg-content" v-if="zone.url && zone.svg !== 'error'">
                            <div class="svg-content__loader">
                                <i class="icon icon--common-spinner spinner" :class="{ 'spinner--finished': zone.svg }"></i>
                            </div>
                            <div v-if="zone.svg" class="svg-content__inner">
                                <transition name="fade">
                                    <div class="svg-content__inner__loader" v-if="loading">
                                        <i class="icon icon--common-spinner spinner"></i>
                                    </div>
                                </transition>

                                <div v-html="zone.svg" :ref="`svg-container-${index}`"></div>
                            </div>
                        </div>
                        <div class="not-found" v-else>
                            <h4>{{ $t('hydraulics.not_found') }}</h4>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else>
                <div class="not-found">
                    <h4>{{ $t('hydraulics.not_found') }}</h4>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import { mapState } from 'vuex';
import axios from 'axios';
import { simpleAxiosInstance } from '@/api/config';
import datapointMixin from '@/mixins/datapoint';
import PageHeader from '@/components/PageHeader';
import PageSubnavigation from '@/components/PageSubnavigation.vue';
import generalLinks from '@/mixins/generalLinks';

export default {
    name: 'system-hydraulics',
    components: {
        PageHeader,
        PageSubnavigation,
    },
    mixins: [datapointMixin, generalLinks],
    data() {
        return {
            systemStructure: null,
            activeZoneIndex: 0,
            zones: [],
            loading: false,
            checkInterval: false,
            intervals: {},
        };
    },
    computed: {
        ...mapState(['activeSystem']),
        activeZone() {
            return this.zones[this.activeZoneIndex];
        },
    },
    created() {
        this.prepareZones();
    },
    destroyed() {
        this.clearCheckIntervals();
    },
    methods: {
        async fetchSystemData(id) {
            try {
                const response = await api.systems.get({ id });
                return response;
            } catch (error) {
                console.error(error);
            }
        },
        async loadDatapointData({ id, node_id, function_id, oid, endpoint = 'datapoint' }) {
            const CancelToken = axios.CancelToken;
            const source = CancelToken.source();
            try {
                const response = await api.systems.datapoint({
                    endpoint: endpoint,
                    id: id,
                    node_id: node_id,
                    function_id: function_id,
                    oid: oid,
                    cancelToken: source.cancelToken,
                });
                return response;
            } catch (error) {
                console.error(error);
            }
        },
        reload() {
            this.clearCheckIntervals();
            this.loadAllDatapoints();
        },
        async prepareZones() {
            if (!this.activeSystem) return;
            this.zones = [];
            if (this.activeSystem.hydraulics_visualization_url) {
                this.zones.push({
                    systemId: this.activeSystem.id,
                    name: this.activeSystem.name,
                    url: this.activeSystem.hydraulics_visualization_url,
                    svg: false,
                    datapointMap: {},
                    reloadIntervals: {
                        30: [],
                    },
                });
            }
            if (this.activeSystem.zones && this.activeSystem.zones.length > 0) {
                await Promise.all(
                    this.activeSystem.zones.map(async zone => {
                        const zoneSystem = await this.fetchSystemData(zone.id);
                        if (zoneSystem.data) {
                            const hasVisualizationUrl = zoneSystem.data.hydraulics_visualization_url !== undefined && zoneSystem.data.hydraulics_visualization_url !== false;
                            this.zones.push({
                                systemId: zoneSystem.data.id,
                                name: zoneSystem.data.name,
                                url: hasVisualizationUrl ? zoneSystem.data.hydraulics_visualization_url : false,
                                svg: false,
                                datapointMap: {},
                                reloadIntervals: {
                                    30: [],
                                },
                            });
                        }
                    })
                );
            }
            this.prepareContent();
        },
        prepareContent() {
            if (!this.zones[this.activeZoneIndex] || !this.zones[this.activeZoneIndex].url || this.zones[this.activeZoneIndex].svg) {
                this.loadAllDatapoints();
            } else {
                this.loadSVGContent();
            }
        },
        async loadSVGContent() {
            const url = this.zones[this.activeZoneIndex].url;
            if (url) {
                try {
                    const response = await simpleAxiosInstance.get(url);
                    if (response.data) {
                        const svgHtml = this.removeTitleTags(response.data);
                        this.zones[this.activeZoneIndex].svg = svgHtml;
                        this.loading = true;
                        setTimeout(() => {
                            this.getOidsFromSVG();
                        }, 100);
                    }
                } catch (error) {
                    console.error(error);
                    this.zones[this.activeZoneIndex].svg = 'error';
                }
            } else {
                this.zones[this.activeZoneIndex].svg = 'error';
            }
        },
        removeTitleTags(svg) {
            const div = document.createElement('div');
            div.innerHTML = svg;
            const titles = div.querySelectorAll('title');
            if (titles) {
                titles.forEach(title => {
                    title.remove();
                });
            }
            return div.innerHTML;
        },
        getOidsFromSVG() {
            const svgContainerEl = this.$refs[`svg-container-${this.activeZoneIndex}`][0];
            if (!svgContainerEl) return;
            const svgEl = svgContainerEl.querySelector('svg');
            if (!svgEl) return;
            const dataElements = svgEl.querySelectorAll('[wh-data]');
            if (dataElements.length > 0) {
                dataElements.forEach(dataEl => {
                    const oidData = dataEl.getAttribute('wh-data');
                    const type = dataEl.hasAttribute('wh-type') ? dataEl.getAttribute('wh-type') : false;
                    const reloadInterval = dataEl.hasAttribute('wh-reload') ? dataEl.getAttribute('wh-reload') : false;
                    if (oidData) {
                        if (!this.activeZone.datapointMap[oidData]) {
                            const oidString = oidData.split(':')[1].substring(1);
                            const oidSubParts = oidString.split('/');
                            const datapoint = {
                                selector: `[wh-data="${oidData}"]`,
                                id: this.activeZone.systemId,
                                node_id: oidSubParts[1],
                                function_id: oidSubParts[2],
                                oid: `${oidSubParts[3]}/${oidSubParts[4]}`,
                                reload: reloadInterval ? [reloadInterval] : [30],
                            };
                            if (type) {
                                datapoint.endpoint = type;
                            }

                            this.activeZone.datapointMap[oidData] = datapoint;
                        } else if (reloadInterval) {
                            this.activeZone.datapointMap[oidData].reload.push(reloadInterval);
                        }
                    }
                });
            }
            this.prepareReloadIntervals();
            this.loadAllDatapoints();
        },
        prepareReloadIntervals() {
            for (const oidData in this.activeZone.datapointMap) {
                const reloadValues = this.activeZone.datapointMap[oidData].reload;
                const timeindex = Math.min(...reloadValues);
                if (!this.activeZone.reloadIntervals[timeindex]) this.activeZone.reloadIntervals[timeindex] = [];
                this.activeZone.reloadIntervals[timeindex].push(oidData);
            }
        },
        async loadAllDatapoints() {
            this.loading = true;
            const datapointKeys = Object.keys(this.activeZone.datapointMap);

            await Promise.all(
                datapointKeys.map(async datapointKey => {
                    const datapoint = this.activeZone.datapointMap[datapointKey];
                    const response = await this.loadDatapointData(datapoint);
                    if (response) {
                        const data = response.data;
                        this.updateContentInSVG(datapoint, data);
                    }
                })
            );
            this.loading = false;
            this.clearCheckIntervals();
            this.startCheckIntervals();
        },
        async loadSingleDatapoint(oidData) {
            const datapoint = this.activeZone.datapointMap[oidData];
            if (datapoint) {
                const response = await this.loadDatapointData(datapoint);
                if (response) {
                    const data = response.data;
                    this.updateContentInSVG(datapoint, data);
                }
            }
        },
        updateContentInSVG(datapoint, data) {
            const svgContainerEl = this.$refs[`svg-container-${this.activeZoneIndex}`][0];
            if (svgContainerEl) {
                const dataElements = svgContainerEl.querySelectorAll(datapoint.selector);
                dataElements.forEach(dataEl => {
                    this.updateDataElement(dataEl, datapoint, data);
                });
            }
        },
        updateDataElement(dataEl, datapoint, data) {
            let updateAbleEl = dataEl;
            if (dataEl.nodeName === 'text' || dataEl.nodeName === 'tspan') {
                // if (dataEl.hasChildNodes()) {
                //     updateAbleEl = dataEl.lastsChild;
                // }
                this.updateTextContent(updateAbleEl, datapoint, data);
            } else if (dataEl.hasAttribute('wh-style')) {
                this.updateStyle(dataEl, datapoint, data);
            }
        },
        updateTextContent(dataEl, datapoint, data) {
            const type = this.getDatapointType(data, datapoint.oid);
            const bitmaskValue = this.getDatapointBitmaskValue(data);
            const unit = this.getDatapointUnit(data);
            const infoString = this.getDatapointLabel(type, data.value, datapoint.oid, bitmaskValue, unit);
            dataEl.innerHTML = infoString;
        },
        updateStyle(dataEl, datapoint, data) {
            const value = parseInt(data.value);
            const styles = JSON.parse(dataEl.getAttribute('wh-style'));
            let newStyles = false;
            for (const key in styles) {
                if (value >= parseInt(key)) {
                    newStyles = styles[key];
                }
            }
            if (newStyles) {
                newStyles = newStyles.split(';');
                newStyles.forEach(newStyle => {
                    if (newStyle !== '') {
                        const splitStyle = newStyle.split(':');
                        dataEl.style[splitStyle[0]] = splitStyle[1];
                    }
                });
            }
        },
        switchTab(index) {
            if (index === this.activeZoneIndex) return;
            this.activeZoneIndex = index;
            this.clearCheckIntervals();
            this.prepareContent();
        },
        startCheckIntervals() {
            for (const i in this.activeZone.reloadIntervals) {
                if (i > 0) {
                    const oidsToReload = [...new Set(this.activeZone.reloadIntervals[i])];
                    if (this.intervals[i]) {
                        clearInterval(this.intervals[i]);
                    }
                    this.intervals[i] = setInterval(() => {
                        oidsToReload.forEach(oidData => {
                            this.loadSingleDatapoint(oidData);
                        });
                    }, i * 1000);
                }
            }
        },
        clearCheckIntervals() {
            for (const i in this.intervals) {
                if (this.intervals[i]) {
                    clearInterval(this.intervals[i]);
                }
            }
        },
    },
};
</script>

<style lang="scss">
.svg-content {
    text-align: center;
    overflow: hidden;
    max-width: 100%;
    margin-top: 40px;

    &__inner {
        position: relative;
        overflow: auto;

        &__loader {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(white, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
}
</style>
